import { Box, Button, Grid, Group, Text, Title } from "@mantine/core"
import { DisabledReason, HelpButtonGroup, HelpIcon, nextStepToast } from "@dehub/theme"
import { CaretLeftIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { DeliveryApi } from "../../apis"
import { IGeneral } from "../../store/General"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import { usePermission } from "../permission"
import { OrderStatusTag } from "../purchase-sales/status-tag"
import classes from "./form.module.css"
import { SupplierDropShippingProductForm } from "./product-form"

export type IFormData = {
  senderNote: string
  senderAdditionalNote: string
  attachments: any[]
}

function Field({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <Box mb="md">
      <Text className={classes.label}>{label}</Text>
      <Text className={classes.value} ff={mono ? "monospace" : undefined}>{children || "-"}</Text>
    </Box>
  )
}

const partnerLine = (b?: { refCode?: string | null; partner?: { refCode?: string | null; businessName?: string | null } | null } | null) =>
  [b?.partner?.refCode, b?.partner?.businessName].filter(Boolean).join(" ")
const businessLine = (b?: { refCode?: string | null } | null) => [b?.refCode, renderBusinessName(b as never, "")].filter(Boolean).join(" ")

/** Dropship detail (Ord_DropShipDetail): three party cards, then the order lines. */
export function SupplierDropShippingForm({ action, before }: { action: any; before?: React.ReactNode }) {
  const { t } = useTranslation(["supplierSales", "common"])
  const params = useParams()
  const navigate = useNavigate()
  const order = action?.[1]
  const { deliveryDateTypes, deliveryNoteStatus } = useSelector((s: { general: IGeneral }) => s.general)
  const [loading, setLoading] = React.useState(false)
  const ORD_DRSHPING_DN_ASSIGN = usePermission("ORD_DRSHPING_DN_ASSIGN")
  const ORD_DN_CREATE = usePermission("ORD_DN_CREATE") // the call itself (BE delivery-note/create.ts)

  // «Хүргэлт хуваарилах» is for the delivery tab, once, while no delivery note exists (BE delivery-note/create.ts).
  // The permission is a reason on its own; an existing delivery note is another one.
  const alreadyAssigned = !!order?.deliveryNote
  const assignReason = ORD_DRSHPING_DN_ASSIGN.reason ?? ORD_DN_CREATE.reason ?? (alreadyAssigned ? t("supplierSales:dropshipping.alreadyAssigned") : null)
  const assignCode = ORD_DRSHPING_DN_ASSIGN.reason || ORD_DN_CREATE.reason ? "PERM" : alreadyAssigned ? "HAS_DN" : null

  const onDelivery = async () => {
    setLoading(true)
    try {
      await DeliveryApi.create([params.id])
      nextStepToast("order.supplier-drop-shipping-detail.toast.assign-delivery", { fallback: { title: t("supplierSales:customer.requestSuccess") } })
      navigate("/supplier-drop-shipping")
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    }
    setLoading(false)
  }

  const dnStatus = (deliveryNoteStatus || []).find((c: any) => c?.code === order?.deliveryNote?.deliveryNoteStatus)?.name

  return (
    <PageLayout
      title={
        <Group gap="sm" wrap="nowrap">
          <Text span inherit>{t("supplierSales:dropshipping.formTitle")}</Text>
          <OrderStatusTag code={order?.orderStatus} />
        </Group>
      }
      subTitle={t("supplierSales:dropshipping.formSubtitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-drop-shipping", label: t("supplierSales:dropshipping.breadcrumb") },
        { label: t("supplierSales:dropshipping.breadcrumbDetail") },
      ]}
      extra={[
        <Button key="back" variant="default" leftSection={<CaretLeftIcon size={16} />} onClick={() => navigate("/supplier-drop-shipping")}>
          {t("supplierSales:dropshipping.backButton")}
        </Button>,
        ...(params?.type === "delivery"
          ? [
              // State-changing action: attached «?» (R2) and a reason popover while unavailable (R4, @dehub/help).
              <DisabledReason key="delivery" helpKey="order.supplier-drop-shipping-detail.reason.assign-delivery" reason={assignCode} when={!!assignReason} position="bottom-end">
                <HelpButtonGroup helpKey="order.supplier-drop-shipping-detail.action.assign-delivery">
                  <Button data-help-id="action-assign-delivery" loading={loading} onClick={onDelivery}>{t("supplierSales:dropshipping.deliveryDistribute")}</Button>
                </HelpButtonGroup>
              </DisabledReason>,
            ]
          : []),
      ]}>
      <Group gap="xl" mt="xs" mb="md">
        <Text fz={14} c="gray.8">{t("supplierSales:dropshipping.registeredBy")}: <Text span fw={700} c="gray.9">{renderName(order?.orderedUser) || "-"}</Text></Text>
        <Text fz={14} c="gray.8">{t("supplierSales:dropshipping.date")}: <Text span fw={700} c="gray.9" ff="monospace">{order?.createdAt ? dateTimeSecFormat(order.createdAt) : "-"}</Text></Text>
        <Text fz={14} c="gray.8">{t("supplierSales:dropshipping.salesOrderLabel").replace(/:$/, "")}: <Text span fw={700} c="gray.9" ff="monospace">{order?.salesCode || "-"}</Text></Text>
      </Group>

      {before}

      <Grid gap="md" mb="md">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box className={classes.card} h="100%">
            <Title order={3} className={classes.sideTitle}>{t("supplierSales:dropshipping.supplierSide")}<HelpIcon helpKey="order.supplier-drop-shipping-detail.section.supplier-side" variant="section" /></Title>
            <Field label={t("supplierSales:dropshipping.partnerNameLabel").replace(/:$/, "")}>{partnerLine(order?.dropperBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.salesOrderLabel").replace(/:$/, "")}>
              {order?.parent?.salesCode ? <Group gap="xs"><Text span inherit ff="monospace">{order.parent.salesCode}</Text><OrderStatusTag code={order.parent.orderStatus} /></Group> : null}
            </Field>
            <Field label={t("supplierSales:dropshipping.supplierManagerLabel").replace(/:$/, "")}>{renderName(order?.orderedUser)}</Field>
            <Field label={t("supplierSales:dropshipping.businessNameLabel").replace(/:$/, "")}>{businessLine(order?.dropperBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.purchaseOrderLabel").replace(/:$/, "")} mono>{order?.parent?.purchaseCode}</Field>
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box className={classes.card} h="100%">
            <Title order={3} className={classes.sideTitle}>{t("supplierSales:dropshipping.buyerSide")}<HelpIcon helpKey="order.supplier-drop-shipping-detail.section.buyer-side" variant="section" /></Title>
            <Field label={t("supplierSales:dropshipping.buyerPartnerName").replace(/:$/, "")}>{partnerLine(order?.receiverBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.buyerCustomerName").replace(/:$/, "")}>{businessLine(order?.receiverBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.taxNumberLabel").replace(/:$/, "")} mono>{order?.receiverBusiness?.regNumber}</Field>
            <Field label={t("supplierSales:dropshipping.paymentTermLabel").replace(/:$/, "")}>{order?.paymentTerm?.description}</Field>
            <Field label={t("supplierSales:dropshipping.receiverStaff").replace(/:$/, "")}>{renderName(order?.receiverStaff)}</Field>
            <Field label={t("supplierSales:dropshipping.receiverBranchLabel").replace(/:$/, "")}>{order?.receiverBranch?.name}</Field>
            <Field label={t("supplierSales:dropshipping.receiverAddress").replace(/:$/, "")}>{order?.receiverBranch?.branchAddress}</Field>
            <Field label={t("supplierSales:dropshipping.orderReceive").replace(/:$/, "")}>
              {deliveryDateTypes.find((c: any) => c?.code === order?.deliveryType)?.name}
            </Field>
            <Field label={t("supplierSales:dropshipping.nearestDeliveryDay").replace(/:$/, "")} mono>
              {order?.deliveryDate ? dateFormat(order.deliveryDate) : null}
            </Field>
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box className={classes.card} h="100%">
            <Title order={3} className={classes.sideTitle}>{t("supplierSales:dropshipping.deliverySide")}</Title>
            <Field label={t("supplierSales:dropshipping.deliveryPartnerName").replace(/:$/, "")}>{partnerLine(order?.dropshipperBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.deliveryOrderLabel").replace(/:$/, "")} mono>{order?.salesCode}</Field>
            <Field label={t("supplierSales:dropshipping.deliverySupplierManager").replace(/:$/, "")}>{renderName(order?.supplierStaff)}</Field>
            <Field label={t("supplierSales:dropshipping.deliveryBusinessName").replace(/:$/, "")}>{businessLine(order?.dropshipperBusiness)}</Field>
            <Field label={t("supplierSales:dropshipping.deliveryStatusLabel").replace(/:$/, "")}>{dnStatus}</Field>
            <Field label={t("supplierSales:dropshipping.deliveryNo")} mono>{order?.deliveryNote?.refCode}</Field>
          </Box>
        </Grid.Col>
      </Grid>

      <Box className={classes.card}>
        <Title order={3} className={classes.cardTitle}>{t("supplierSales:dropshipping.orderItemsTitle")}</Title>
        <SupplierDropShippingProductForm action={action} setFieldValue={() => undefined} values={{ senderNote: "", senderAdditionalNote: "", attachments: [], ...order }} />
      </Box>
    </PageLayout>
  )
}
